<template>
  <sds-card title="Jurisprudência Selecionada" @keydown.enter="consultar">
    <v-row class="ma-0">
      <v-col cols="12" class="pa-0">
        <v-label for="filtro"> Pesquisa por Termos*: </v-label>
        <v-text-field
          v-model.trim="filtroJurisprudencia.dePesquisaTermos"
          dense
          outlined
          hide-details
          clearable
          placeholder="Busca decisões que contenham as palavras/expressões desejadas no seu inteiro teor."
        />
      </v-col>
    </v-row>

    <v-row class="ma-0 pt-2 d-flex justify-start align-center">
      <div
        v-for="(item, index) in OPERADORES"
        :key="index + item.operador"
        class="custom-chip"
      >
        <CustomChip
          :text="item.value"
          :text-tooltip="item.tooltip"
          @click="handleClickTermos(item)"
        />
      </div>
      <v-checkbox
        v-model="filtroJurisprudencia.flEmenta"
        hide-details
        label="Ementa"
        class="ma-0 ml-4 pa-0"
      >
      </v-checkbox>
      <v-checkbox
        v-model="filtroJurisprudencia.flInteiroTeor"
        hide-details
        label="Inteiro Teor"
        class="ma-0 ml-4 pa-0"
      >
      </v-checkbox>
    </v-row>

    <v-row>
      <v-col>
        <CardMaisFiltros />
      </v-col>
    </v-row>

    <v-divider class="mt-8 mb-1"></v-divider>

    <v-card-actions class="pa-0 pt-2">
      <v-col class="d-flex justify-end pa-0">
        <v-btn text color="primary" @click="limpar"> Limpar </v-btn>
        <v-btn color="primary" class="ml-2" @click="consultar">
          Pesquisar
        </v-btn>
      </v-col>
    </v-card-actions>
  </sds-card>
</template>

<script lang="ts" setup>
import { onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import CardMaisFiltros from './CardMaisFiltros.vue';
import { ConsultaJurisprudenciaSelecionadaStore } from '../../store/ConsultaJurisprudenciaSelecionadaStore';
import CustomChip from '@/views/components/CustomChip.vue';
import { OPERADORES } from '@/constants/operadores';
import { useRoute } from 'vue-router/composables';
import { GET_CARTORIOS } from '@/queries';
import { useLazyQuery } from '@vue/apollo-composable';
import SdsSnackbarQueue from '@ungp/sds-snackbar-queue';
import { useLoader } from '@/composables/useLoader';
import { getISOFromDate } from '@/utils/DateUtils';

interface IOperador {
  operador: string;
  tooltip: string;
  value: string;
}

const store = ConsultaJurisprudenciaSelecionadaStore();

const { filtroJurisprudencia, pagina } = storeToRefs(store);
const { consultaJurisprudenciaSelecionada, limparTodosFiltroJurisprudencia } =
  store;

const loader = useLoader();

const { query } = useRoute();

const getSecao = useLazyQuery(GET_CARTORIOS);

onMounted(async () => {
  const dtJulgamentoInicio = getISOFromDate(String(query.dtJulgamentoini));
  const dtJulgamentoFim = getISOFromDate(String(query.dtJulgamentofim));
  const expandirPesquisaAvancada = Boolean(Object.keys(query).length);

  filtroJurisprudencia.value.dtJulgamentoInicio = dtJulgamentoInicio;
  filtroJurisprudencia.value.dtJulgamentoFim = dtJulgamentoFim;
  filtroJurisprudencia.value.expandirPesquisaAvancada =
    expandirPesquisaAvancada;

  await buscarSecao();
});

const buscarSecao = async () => {
  const deTipoCartorio = `${query.secao}`.replace(/^"(.*)"$/, '$1');
  getSecao.variables.value = { deTipoCartorio };

  if (!getSecao.variables.value) return;
  try {
    loader.startLoader();
    await getSecao.load();
    filtroJurisprudencia.value = {
      ...filtroJurisprudencia.value,
      secao: getSecao.result.value?.tipoCartorios ?? null,
    };
  } catch {
    SdsSnackbarQueue.error(
      getSecao.error.value?.message ?? 'Não foi possível pesquisar os boletins'
    );
  } finally {
    loader.dismissLoader();
  }
};

const handleClickTermos = (termo: IOperador) => {
  let dePesquisa = filtroJurisprudencia.value.dePesquisaTermos;

  const { operador } = termo;

  switch (operador) {
    case 'Aspas':
      dePesquisa = dePesquisa ? '"' + dePesquisa + '"' : '""';
      break;
    case 'Interrogacao':
      dePesquisa = dePesquisa ? dePesquisa + '?' : '?';
      break;
    case 'Asterisco':
      dePesquisa = dePesquisa ? dePesquisa + '*' : '*';
      break;
    default:
      dePesquisa = dePesquisa ? `${dePesquisa} ${operador} ` : operador;
  }

  filtroJurisprudencia.value = {
    ...filtroJurisprudencia.value,
    dePesquisaTermos: dePesquisa,
  };
};

const consultar = () => {
  pagina.value = 1;
  consultaJurisprudenciaSelecionada(true);
};

const limpar = () => {
  filtroJurisprudencia.value.expandirPesquisaAvancada = false;
  limparTodosFiltroJurisprudencia();
};
</script>
<style lang="scss" scoped>
.custom-chip {
  margin-left: 8px;
}
.custom-chip:first-child {
  margin-left: 0px;
}
</style>
